"use client";

import Image from "next/image";
import { useRef, useState } from "react";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { useInViewAnimation } from "../animation/useInViewAnimation";
import { useTranslation } from "next-i18next";

export default function Kontakt() {
  const { t } = useTranslation("kontakt");
  const ref = useRef(null);
  const inView = useInViewAnimation(ref);
  const { basePath } = useRouter();

  const [buttonHover, setButtonHover] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [botField, setBotField] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    firmenname: "",
    email: "",
    telefonnummer: "",
    nachricht: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    telefonnummer: "",
    nachricht: "",
    consent: "",
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const telefonnummerRegex = /^[0-9()+\-\s]*$/;

  function validateField(name, value) {
    switch (name) {
      case "name":
        return !value.trim() ? t("validation.nameRequired") : "";
      case "email":
        if (!value.trim()) return t("validation.emailRequired");
        if (!emailRegex.test(value)) return t("validation.emailInvalid");
        return "";
      case "telefonnummer":
        return value && !telefonnummerRegex.test(value)
          ? t("validation.phoneInvalid")
          : "";
      case "nachricht":
        return !value.trim() ? t("validation.messageRequired") : "";
      case "consent":
        return !consent ? t("validation.consentRequired") : "";
      default:
        return "";
    }
  }

  function validateAll(data) {
    return {
      name: validateField("name", data.name),
      email: validateField("email", data.email),
      telefonnummer: validateField("telefonnummer", data.telefonnummer),
      nachricht: validateField("nachricht", data.nachricht),
      consent: validateField("consent", consent),
    };
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitted) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    setSubmitted(true);
    setShowSuccess(false);

    const validationErrors = validateAll(formData);
    setErrors(validationErrors);

    if (botField) return;

    const isValid = Object.values(validationErrors).every((err) => err === "");
    if (!isValid) return;

    setLoading(true);
    try {
      const payload = {
        "form-name": "contact",
        companyWebsite: botField || "",
        name: formData.name,
        firmenname: formData.firmenname,
        email: formData.email,
        telefonnummer: formData.telefonnummer,
        nachricht: formData.nachricht,
        consent: consent ? "true" : "",
      };

      const res = await fetch("/netlify-forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      });

      if (!res.ok) throw new Error(`Netlify form submit failed: ${res.status}`);

      setFormData({
        name: "",
        firmenname: "",
        email: "",
        telefonnummer: "",
        nachricht: "",
      });
      setConsent(false);
      setSubmitted(false);
      setErrors({
        name: "",
        email: "",
        telefonnummer: "",
        nachricht: "",
        consent: "",
      });
      setShowSuccess(true);
    } catch (err) {
      console.error(err);
      alert(t("error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="contact" ref={ref} className="w-full bg-reefliBeige pt-12">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="w-full max-w-6xl mx-auto bg-white rounded-none md:rounded-2xl overflow-hidden flex flex-col lg:flex-row shadow-lg"
      >
        {/* левая часть с изображениями */}
        <div className="relative w-full lg:w-2/5 min-h-[400px]">
          <div className="block lg:hidden w-full h-full">
            <Image
              src={`${basePath}/img/kontakt_mobile.jpg`}
              alt="Produkte Mobile"
              fill
              className="object-cover"
              loading="lazy"
              unoptimized
            />
          </div>
          <div className="hidden lg:block w-full h-full">
            <Image
              src={`${basePath}/img/IMG_0876.jpg`}
              alt="Produkte"
              fill
              className="object-cover"
              loading="lazy"
              unoptimized
            />
          </div>
          <div className="absolute top-6 left-6 text-[#262104] text-2xl font-extrabold font-montserrat">
            REEFLI
            <span className="align-super text-[0.65em] font-sans ml-0.5">
              ™
            </span>
          </div>
        </div>

        <div className="w-full lg:w-3/5 p-8 md:p-12 text-[#262104]">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
            {t("title")}
          </h2>

          <div className="flex flex-col md:flex-row justify-between gap-2 text-sm md:text-base font-medium mb-4">
            <p>
              <span className="font-bold">{t("email")}:</span>{" "}
              <a href="mailto:support@reefli.com" className="underline">
                support@reefli.com
              </a>
            </p>
            <p>
              <span className="font-bold">{t("phone")}:</span>{" "}
              <a href="tel:07223-9579977" className="underline">
                07223-9579977
              </a>
            </p>
          </div>

          <div className="flex flex-col gap-4 mb-6 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <Image
                src={`${basePath}/img/instagram.svg`}
                alt="Instagram"
                width={20}
                height={20}
                className="inline-block"
                unoptimized
              />
              <a
                href="https://www.instagram.com"
                className="underline font-semibold"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("instagram")}
              </a>
            </div>

            <div className="flex items-center gap-2">
              <Image
                src={`${basePath}/img/whatsapp.svg`}
                alt="Whatsapp"
                width={20}
                height={20}
                className="inline-block"
                unoptimized
              />
              <a
                href="https://wa.me/4972239579977" // номер без "+" и пробелов
                className="underline font-semibold"
                target="_blank"
                rel="noopener noreferrer"
              >
                Whatsapp
              </a>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-center mb-4">
            {t("formTitle")}
          </h3>

          {/* AJAX → статический маршрут Netlify */}
          <form
            name="contact"
            method="POST"
            data-netlify="true"
            action="/netlify-forms.html"
            data-netlify-honeypot="companyWebsite"
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4"
          >
            <input type="hidden" name="form-name" value="contact" />
            <input
              type="text"
              name="companyWebsite"
              value={botField}
              onChange={(e) => setBotField(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <input
                  type="text"
                  name="name"
                  placeholder={t("fields.name")}
                  value={formData.name}
                  onChange={handleChange}
                  className={`border-2 rounded-xl px-4 py-2 text-sm md:text-base ${
                    submitted && errors.name
                      ? "border-red-600"
                      : "border-[#265e738d] focus:border-[#b99878]"
                  }`}
                />
                <span
                  className={`text-xs mt-1 ${
                    submitted && errors.name ? "text-red-600" : "invisible"
                  }`}
                >
                  {errors.name || " "}
                </span>
              </div>

              <div className="flex flex-col">
                <input
                  type="text"
                  name="firmenname"
                  placeholder={t("fields.company")}
                  value={formData.firmenname}
                  onChange={handleChange}
                  className="border-2 border-[#265e738d] rounded-xl px-4 py-2 text-sm md:text-base"
                />
                <span className="invisible text-xs mt-1"> </span>
              </div>

              <div className="flex flex-col">
                <input
                  type="email"
                  name="email"
                  placeholder={t("fields.email")}
                  value={formData.email}
                  onChange={handleChange}
                  className={`border-2 rounded-xl px-4 py-2 text-sm md:text-base ${
                    submitted && errors.email
                      ? "border-red-600"
                      : "border-[#265e738d] focus:border-[#b99878]"
                  }`}
                />
                <span
                  className={`text-xs mt-1 ${
                    submitted && errors.email ? "text-red-600" : "invisible"
                  }`}
                >
                  {errors.email || " "}
                </span>
              </div>

              <div className="flex flex-col">
                <input
                  type="tel"
                  name="telefonnummer"
                  placeholder={t("fields.phone")}
                  value={formData.telefonnummer}
                  onChange={handleChange}
                  className={`border-2 rounded-xl px-4 py-2 text-sm md:text-base ${
                    submitted && errors.telefonnummer
                      ? "border-red-600"
                      : "border-[#265e738d] focus:border-[#b99878]"
                  }`}
                />
                <span
                  className={`text-xs mt-1 ${
                    submitted && errors.telefonnummer
                      ? "text-red-600"
                      : "invisible"
                  }`}
                >
                  {errors.telefonnummer || " "}
                </span>
              </div>
            </div>

            <div className="flex flex-col">
              <textarea
                name="nachricht"
                placeholder={t("fields.message")}
                rows={8}
                value={formData.nachricht}
                onChange={handleChange}
                className={`w-full border-2 rounded-xl px-4 py-2 text-sm md:text-base resize-none ${
                  submitted && errors.nachricht
                    ? "border-red-600"
                    : "border-[#265e738d] focus:border-[#b99878]"
                }`}
              />
              <span
                className={`text-xs mt-1 ${
                  submitted && errors.nachricht ? "text-red-600" : "invisible"
                }`}
              >
                {errors.nachricht || " "}
              </span>
            </div>

            <label className="flex items-start gap-2 text-sm md:text-base">
              <input
                type="checkbox"
                name="consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
              />
              <span>
                {t("consent.text")}{" "}
                <a
                  href={`${basePath}/datenschutz`}
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("consent.link")}
                </a>
              </span>
            </label>
            {submitted && errors.consent ? (
              <span className="text-red-600 text-xs -mt-2">
                {errors.consent}
              </span>
            ) : (
              <span className="invisible text-xs -mt-2"> </span>
            )}

            <div className="text-right">
              <button
                type="submit"
                disabled={loading}
                onMouseEnter={() => setButtonHover(true)}
                onMouseLeave={() => setButtonHover(false)}
                className={`relative inline-block overflow-hidden px-6 py-2 rounded-full shadow-md text-sm md:text-base font-semibold group border-2 border-[#382806] ${
                  loading
                    ? "opacity-70 cursor-not-allowed bg-[#382806] text-white"
                    : "bg-[#382806] text-white"
                }`}
              >
                <span
                  className={`relative z-10 ${
                    buttonHover ? "text-[#262104]" : "text-white"
                  }`}
                >
                  {loading ? t("sending") : t("fields.submit")}
                </span>
                <span
                  className={`absolute inset-0 bg-white z-0 rounded-full transition-transform duration-500 ${
                    buttonHover ? "translate-y-0" : "translate-y-full"
                  }`}
                />
              </button>
            </div>
          </form>

          {showSuccess && (
            <div
              className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-center font-medium shadow-sm"
              dangerouslySetInnerHTML={{ __html: t("successMessage") }}
            />
          )}
        </div>
      </motion.div>
    </section>
  );
}
